import dynamic from "next/dynamic"
import React from "react"
import { useRouter } from "next/router"
import useWindowSize from "hooks/useWindowSize"

const LoadingImage = dynamic(
    () => import("components/atoms/Image/LoadingImage"),
    {
        ssr: false,
    }
)

const EventPageSkeleton = () => {
    const { isDesktop } = useWindowSize()
    const router = useRouter()
    const isF1Page = typeof router?.asPath === 'string' && /\/events\/(f1)(?:$|\?|#)/.test(router.asPath)

    // Match the real page hero: fixed background, aspect-video on mobile, 100vh on desktop
    const heroStyle: React.CSSProperties = {
        height: isDesktop ? "100vh" : "56.25vw",
    }

    // Match the real page content offset to avoid layout shifts
    const contentOffset: React.CSSProperties = {
        marginTop: isDesktop ? (isF1Page ? "calc(100vh - 250px)" : "calc(100vh - 100px)") : "56.25vw",
    }

    return (
        <div className="relative">
            {/* Fixed hero placeholder (md+) to mirror EventHero positioning */}
            <div
                className="fixed left-0 top-0 -z-10 aspect-video max-h-screen lg:h-screen w-full overflow-hidden md:block"
                style={heroStyle}
            >
                <div className="flex h-full w-full items-center justify-center bg-gray-200 dark:bg-gray-800">
                    <LoadingImage />
                </div>
            </div>

            {/* Content container with the same top offset as the real page */}
            <div className="mx-auto max-w-screen-xl transition-all duration-300" style={contentOffset}>
                <div className="animate-pulse">
                    {/* Desktop spacer to match real layout */}
                    <div className="hidden md:block h-[100px]" />

                    {/* Header skeleton matching EventHeader layout */}
                    <div className="border border-[#B5B5B5] dark:border-white/50 rounded-md p-2 lg:py-6 flex flex-col lg:flex-row">
                        {/* Right (title + share) - 80% on lg */}
                        <div className="flex w-full lg:w-[80%] items-center lg:items-stretch lg:order-2">
                            <div className="relative flex flex-col justify-center items-center lg:items-start w-full lg:pl-10">
                                <div className="hidden lg:block absolute top-[17%] bottom-[17%] right-0 w-px bg-[#B5B5B5] dark:bg-white/50" />
                                <div className="h-7 w-2/3 lg:w-1/2 rounded-full bg-gray-200 dark:bg-gray-700" />
                            </div>
                            <div className="hidden md:flex flex-col items-center justify-center w-[50%] px-2">
                                <div className="h-6 w-40 rounded-full bg-gray-200 dark:bg-gray-700" />
                            </div>
                        </div>
                        {/* Left (date/time) - 20% on lg */}
                        <div className="flex items-center justify-center w-full lg:w-[20%] lg:order-1 px-2 pb-4 lg:py-2">
                            <div className="relative flex flex-col items-center w-1/2 px-2">
                                <div className="h-6 w-6 rounded bg-gray-200 dark:bg-gray-700 mb-1" />
                                <div className="h-3 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                                <div className="absolute top-0 bottom-0 right-0 w-px bg-[#B5B5B5] dark:bg-white/50" />
                            </div>
                            <div className="flex flex-col items-center w-1/2 px-2">
                                <div className="h-4 w-20 rounded-full bg-gray-200 dark:bg-gray-700 mb-1" />
                                <div className="h-3 w-14 rounded-full bg-gray-200 dark:bg-gray-700" />
                            </div>
                        </div>
                    </div>

                    {/* Sections wrapper: description, (videos), location, gallery */}
                    <div className="sections-wrapper flex flex-col gap-y-4 mx-4 md:mx-0 mt-10">
                        {/* Description */}
                        <div className="section flex flex-col gap-y-1 lg:flex-row">
                            <div className="lg:w-[20%] h-4 w-24 rounded-full bg-gray-200 dark:bg-gray-700 mb-2" />
                            <div className="lg:w-[60%] flex flex-col gap-3">
                                <div className="h-3 w-full rounded-full bg-gray-200 dark:bg-gray-700" />
                                <div className="h-3 w-full rounded-full bg-gray-200 dark:bg-gray-700" />
                                <div className="h-3 w-5/6 rounded-full bg-gray-200 dark:bg-gray-700" />
                                <div className="h-3 w-2/3 rounded-full bg-gray-200 dark:bg-gray-700" />
                            </div>
                            <div className="lg:w-[20%]" />
                        </div>

                        {/* Location */}
                        <div className="section flex flex-col gap-y-1 lg:flex-row border-t border-t-white/20 lg:border-none py-4">
                            <div className="lg:w-[20%] h-4 w-24 rounded-full bg-gray-200 dark:bg-gray-700 mb-2" />
                            <div className="lg:w-[60%]">
                                <div className="h-40 w-full rounded-md bg-gray-200 dark:bg-gray-700" />
                            </div>
                            <div className="lg:w-[20%]" />
                        </div>

                        {/* Gallery */}
                        <div className="section flex flex-col gap-y-1 lg:flex-row border-t border-t-white/20 lg:border-none py-4">
                            <div className="lg:w-[20%] h-4 w-24 rounded-full bg-gray-200 dark:bg-gray-700 mb-2" />
                            <div className="lg:w-[60%] grid grid-cols-2 md:grid-cols-3 gap-2">
                                <div className="h-28 rounded-md bg-gray-200 dark:bg-gray-700" />
                                <div className="h-28 rounded-md bg-gray-200 dark:bg-gray-700" />
                                <div className="h-28 rounded-md bg-gray-200 dark:bg-gray-700" />
                            </div>
                            <div className="lg:w-[20%]" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky bottom action bar skeleton to match ActionCard */}
            <div className="sticky bottom-0 left-0 right-0 mx-auto flex w-full max-w-screen-xl items-center justify-between px-5 gap-y-3 py-3 border border-b-0 border-white/20 backdrop-blur-sm dark:bg-primary-theme-400/50 rounded-t-xl animate-pulse">
                <div className="h-5 w-40 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-10 w-44 rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>
        </div>
    )
}

export default EventPageSkeleton
